import styled from "styled-components/macro";

import {useState} from "react";
import {useParams} from "react-router-dom";
import Header from "../../components/header";
import TopBar from "../../components/top_bar";
import SortBar from "../../components/sort_bar";
import CommunityHeader from "../../components/community_header";
import Container from "../../components/container";
import Main from '../../components/main';
import Sidebar from "../../components/sidebar";
import Footer from "../../components/footer";
import Wrapper from "../../components/wrapper";
import {useCommunityStyle} from "../../api/communities";
import {useBaseCommunity} from "../../store/hooks";
import HeaderData from "../../components/header_data";
import NavLink from "../../components/nav_link";
import {useQuery} from "../../utils/router";
import {useFeedback} from "../../store/feedback";
import {useQueue} from "../../api/content";
import Post from "../../components/post";
import Comment from "../../components/comment";
import PostList from "../../components/post_list";
import {useSettings} from "../../store";
import {isPseudoCommunity} from "../../store/communites";
import CommunityInfo from "../../components/community_info";
import {themecolors} from "../../utils/themecolors";
import {Menu, MenuButton, MenuList} from "@chakra-ui/react";
import ProfileSortMenu from "../../components/menus/profile_sort_menu";
import useWindowDimensions from "../../utils/dimensions";
import LegacySortBar from "../../components/legacy/legacy_sort_bar";
import {ChevronDown} from "lucide-react";

const Items = styled.div``;
const Item = styled.div``;
const Manage = styled.div``;
const Sort = styled.div``;
const SortContainer = styled.div``;

const SORT_MAPPING = {
    old: 'Old',
    new: 'New',
    controversial: 'Worst',
    top: 'Top',
}

const QueueList = styled(({className, community, sort, type}) => {
    const loading = useFeedback();
    const {content, more, loadNextPage} = useQueue(loading.channel, community, sort, type);
    const {expand} = useSettings();
    return (
            <div className={className}>
                {content?.map((content, index) => {
                    if (!content) {
                        // eslint-disable-next-line react/no-array-index-key
                        return <Post key={index}/>;
                    }
                    if ('comments' in content) {
                        return (
                            <Post key={content.id} post={content} show_community={community === 'win' || community === 'all' || community === 'home' || community === 'trending'} expand={false} />
                        );
                    }
                    return (
                        <Comment key={content.id} comment={content} showCommunity={community === 'win' || community === 'all' || community === 'home'} contextual/>
                    );
                })}
                {loading.busy && (
                    <PostList
                        initialExpand={false}
                        loading
                    />
                )}
            </div>
    );
})`
  ${Post}, ${Comment} {
    border-bottom: ${({theme}) => theme.standard_border};
  }
  ${Post} {
    padding: 12px 12px 12px 0;
  }
`;

const Queue = styled(({className}) => {
    const baseCommunity = useBaseCommunity();
    const {community: paramsCommunity} = useParams();
    const community = (paramsCommunity || baseCommunity).toLowerCase();
    const style = useCommunityStyle(community);
    const {sort = "new", type = "all", from = ""} = useQuery();
    const sort_name = sort ? SORT_MAPPING[sort.toLowerCase()] : 'New';
    const [sorting, setSorting] = useState(false);
    const handleSort = () => {
        setSorting(!sorting);
    };

    const {width} = useWindowDimensions();

    return (
        <>
            <HeaderData title={`Mod Queue`} community={community} community_style={style}/>
            <Header>
                {(baseCommunity !== "TheDonald" || width <= 780) && <TopBar legacyConversion={baseCommunity === "TheDonald"} />}
                {!isPseudoCommunity(community) &&
                    <>
                        <CommunityHeader community={community}/>
                        {baseCommunity !== "TheDonald" &&
                            <CommunityInfo desktop community={community}/>
                        }
                    </>
                }
                {baseCommunity === "TheDonald" && width > 780 && <LegacySortBar />}
            </Header>
            <SortBar community={community} />
            <Container className={className} forceWidth={"slim"}>
                <Wrapper>
                    <Manage>
                        <Items>
                            <Item className={!type || type === 'all' && 'active'}>
                                <NavLink href={`?type=all`}
                                         react_route>All</NavLink>
                            </Item>
                            <Item className={type === 'posts' && 'active'}>
                                <NavLink href={`?type=posts`}
                                         react_route>Posts</NavLink>
                            </Item>
                            <Item className={!type || type === 'comments' && 'active'}>
                                <NavLink active={!type || type === 'comments'} href={`?type=comments`}
                                         react_route>Comments</NavLink>
                            </Item>
                        </Items>
                        <SortContainer>
                            <Menu>
                                <MenuButton>
                                    <Sort>
                                        Sort by {sort_name}
                                        <ChevronDown size={18} />
                                    </Sort>
                                </MenuButton>
                                <MenuList>
                                    <ProfileSortMenu type={type} from={from} />
                                </MenuList>
                            </Menu>
                        </SortContainer>
                    </Manage>
                    <Main>
                        {(!type || type === 'all') &&
                        <QueueList community={community} sort={sort} type={type}/>}
                        {type === 'posts' &&
                        <QueueList community={community} sort={sort} type={type}/>}
                        {type === 'comments' &&
                        <QueueList community={community} sort={sort} type={type}/>}
                    </Main>
                </Wrapper>
                <Sidebar sidebar={style?.sidebar} community={community} />
            </Container>
            <Footer />
        </>
    );
})`
  ${Item} {
    padding: 6px 16px;
    margin-right: 6px;
    border-radius: 48px;
    white-space: nowrap;
  }
  ${Items} {
    display: flex;
    align-items: center;
    overflow-y: scroll;
    -ms-overflow-style: none;  /* IE and Edge */
    scrollbar-width: none;  /* Firefox */
  }
  ${Items}::-webkit-scrollbar {
    display: none;
  }

  ${Manage} {
    display: flex;
    font-size: 15px;
    margin-bottom: 12px;
  }

  ${Item} {
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_medium_impact_text : themecolors.light_medium_impact_text};
  }
  ${Item}.active {
    background-color: ${({theme}) => theme.color.name === "dark" ? themecolors.dark_background_2 : '#fff'};
  }

  ${SortContainer} {
    margin-left: auto;
    display: flex;
    align-items: center;
  }
  ${Sort} {
    margin-left: auto;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_medium_impact_text : themecolors.light_medium_impact_text};
    cursor: pointer;
    white-space: nowrap;
    display: flex;
    align-items: center;
  }
  ${Sort} svg {
    margin-left: 12px;
  }
  @media only screen and (max-width: 780px) {
    ${Manage} {
      width: 100%;
      margin: 2px 0;
      overflow: scroll;
      -ms-overflow-style: none;  /* IE and Edge */
      scrollbar-width: none;  /* Firefox */
    }
    ${Manage}::-webkit-scrollbar {
      display: none;
    }
    ${Items} {
      padding-left: 12px;
    }
    ${Sort} {
      padding: 12px 24px;
    }
  }

`;

export default Queue;
